import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Web3 } from 'web3';
import EthereumProvider from '@walletconnect/ethereum-provider';
import { getWalletProvider, resolveProvider, switchToBSCNetwork, BSC_MAINNET } from '../utils/walletUtils';

const Web3Context = createContext();

// Normalize a chain id to a Number regardless of hex ('0x38') or decimal ('56'/56) form.
// eth_chainId returns hex, but REACT_APP_CHAIN_ID is often set as decimal — comparing
// the two as raw strings was always unequal, forcing a redundant network switch.
const toChainNumber = (v) => {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim();
  return s.toLowerCase().startsWith('0x') ? parseInt(s, 16) : parseInt(s, 10);
};
const chainIdsEqual = (a, b) => {
  const na = toChainNumber(a);
  const nb = toChainNumber(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export const Web3Provider = ({ children }) => {
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);

  // Prevent listener leaks by attaching exactly one set of provider listeners at a time
  const providerRef = useRef(null);
  const accountsChangedHandlerRef = useRef(null);
  const chainChangedHandlerRef = useRef(null);

  const detachProviderListeners = useCallback((provider) => {
    if (!provider) return;
    try {
      if (accountsChangedHandlerRef.current && provider.removeListener) {
        provider.removeListener('accountsChanged', accountsChangedHandlerRef.current);
      }
      if (chainChangedHandlerRef.current && provider.removeListener) {
        provider.removeListener('chainChanged', chainChangedHandlerRef.current);
      }
    } catch (_) {
      // ignore
    }
    accountsChangedHandlerRef.current = null;
    chainChangedHandlerRef.current = null;
  }, []);

  const disconnectWallet = useCallback(() => {
    // Detach listeners from current provider to avoid MaxListenersExceededWarning
    if (providerRef.current) {
      detachProviderListeners(providerRef.current);
    }
    if (providerRef.current?.disconnect) {
      try {
        providerRef.current.disconnect();
      } catch (_) {
        // ignore
      }
    }
    providerRef.current = null;

    setWeb3(null);
    setAccount(null);
    setChainId(null);
    setCurrentProvider(null);
    setIsConnected(false);
    localStorage.removeItem('walletAddress');
  }, [detachProviderListeners]);

  const attachProviderListeners = useCallback((provider) => {
    if (!provider) return;

    // Detach from any previous provider to ensure we never stack listeners
    if (providerRef.current && providerRef.current !== provider) {
      detachProviderListeners(providerRef.current);
    } else if (providerRef.current === provider) {
      detachProviderListeners(provider);
    }

    providerRef.current = provider;

    const onAccountsChanged = (newAccounts) => {
      if (Array.isArray(newAccounts) && newAccounts.length > 0) {
        setAccount(newAccounts[0]);
        localStorage.setItem('walletAddress', newAccounts[0]);
      } else {
        disconnectWallet();
      }
    };

    const onChainChanged = (newChainId) => {
      setChainId(newChainId);
      window.location.reload();
    };

    accountsChangedHandlerRef.current = onAccountsChanged;
    chainChangedHandlerRef.current = onChainChanged;

    provider.on('accountsChanged', onAccountsChanged);
    provider.on('chainChanged', onChainChanged);
  }, [detachProviderListeners, disconnectWallet]);

  const connectWallet = useCallback(async (walletId = null) => {
    try {
      setIsConnecting(true);

      // Get the provider based on wallet ID or auto-detect
      let provider = null;

      if (walletId === 'walletconnect') {
        const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;
        if (!projectId) {
          throw new Error('WalletConnect is not configured on this platform. Please use MetaMask or Trust Wallet.');
        }
        provider = await EthereumProvider.init({
          projectId,
          chains: [56],
          optionalChains: [56],
          rpcMap: { 56: 'https://bsc-dataseed.bnbchain.org' },
          showQrModal: true
        });
        await provider.connect();
      } else {
        provider = resolveProvider(walletId);
        if (!provider) {
          throw new Error('No wallet provider found. Please install MetaMask, Trust Wallet, Coinbase Wallet, or Binance Wallet.');
        }
      }

      const web3Instance = new Web3(provider);

      // Request account access
      const accounts = await provider.request({
        method: 'eth_requestAccounts'
      });

      if (accounts.length === 0) {
        throw new Error('No accounts found');
      }

      // Check network - use BSC Mainnet as default
      const networkId = await provider.request({
        method: 'eth_chainId'
      });

      // Use BSC Mainnet (chainId: 56 / 0x38) as the expected chain
      const expectedChainId = process.env.REACT_APP_CHAIN_ID || BSC_MAINNET.chainId;

      // Automatically switch to BSC if not already on it
      if (!chainIdsEqual(networkId, expectedChainId)) {
        try {
          await switchToBSCNetwork(provider);
          // Get the updated chain ID after switching
          const updatedChainId = await provider.request({
            method: 'eth_chainId'
          });
          setChainId(updatedChainId);
        } catch (error) {
          console.log('Network switch failed:', error);
          // Continue with connection even if switch fails (user may have rejected)
        }
      }

      setWeb3(web3Instance);
      setAccount(accounts[0]);
      setChainId(networkId);
      setCurrentProvider(provider);
      setIsConnected(true);

      // Store wallet address in localStorage for API access
      localStorage.setItem('walletAddress', accounts[0]);

      attachProviderListeners(provider);

      return true;
    } catch (error) {
      console.error('Wallet connection error:', error);
      throw error; // Let the caller handle the error
    } finally {
      setIsConnecting(false);
    }
  }, [attachProviderListeners]);

  useEffect(() => {
    // Check if already connected on mount
    const checkConnection = async () => {
      const provider = getWalletProvider();
      if (provider) {
        try {
          const accounts = await provider.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            const web3Instance = new Web3(provider);
            
            // Check current network and switch to BSC if needed
            const networkId = await provider.request({ method: 'eth_chainId' });
            const expectedChainId = process.env.REACT_APP_CHAIN_ID || BSC_MAINNET.chainId;

            if (!chainIdsEqual(networkId, expectedChainId)) {
              try {
                await switchToBSCNetwork(provider);
                // Get the updated chain ID after switching
                const updatedChainId = await provider.request({ method: 'eth_chainId' });
                setChainId(updatedChainId);
              } catch (error) {
                console.log('Network switch failed on mount:', error);
                // Continue with current network if switch fails
                setChainId(networkId);
              }
            } else {
              setChainId(networkId);
            }
            
            setWeb3(web3Instance);
            setAccount(accounts[0]);
            setCurrentProvider(provider);
            setIsConnected(true);

            attachProviderListeners(provider);
          }
        } catch (error) {
          if (String(error?.message || '').includes('Invalid method')) {
            // Some wallet providers (e.g. BinanceChain) don't support eth_accounts — safe to ignore.
          } else {
            console.error('Error checking wallet connection:', error);
          }
        }
      }
    };

    checkConnection();
  }, [attachProviderListeners]);

  // Cleanup on unmount (avoid leaks in React strict mode/dev)
  useEffect(() => {
    return () => {
      if (providerRef.current) detachProviderListeners(providerRef.current);
    };
  }, [detachProviderListeners]);

  const value = {
    web3,
    account,
    chainId,
    isConnected,
    isConnecting,
    connectWallet,
    disconnectWallet,
    currentProvider
  };

  return (
    <Web3Context.Provider value={value}>
      {children}
    </Web3Context.Provider>
  );
};
